import type { BondOrder } from './editor';
import type { DrawingLine, NormalizedPoint } from '../model';
import type { PaperSize } from './geometry';

export interface PixelPoint {
  readonly x: number;
  readonly y: number;
}

export interface BondDrawingConfig {
  // Null means no explicit ink choice. #1107 keeps default artifact ink independent
  // of platform appearance; authored colors remain concrete values.
  readonly color: string | null;
  readonly lengthPx: number;
  readonly angleIntervalDegrees: number;
  readonly defaultAngleDegrees: number;
  readonly connectedBondAngleDegrees: number;
  readonly proposalRadiusPx: number;
  readonly proposalHotDistancePx: number;
  readonly proposalSnapDistancePx: number;
  readonly existingEndpointOfferDistancePx: number;
  readonly existingEndpointSnapDistancePx: number;
  readonly touchTargetRadiusPx: number;
  readonly touchOfferDistancePx: number;
}

// Shared by the white drawing paper, custom-color controls and self-contained exports.
export const DEFAULT_INK_COLOR = '#000000';

export const DEFAULT_BOND_DRAWING_CONFIG: BondDrawingConfig = {
  color: null,
  lengthPx: 45,
  angleIntervalDegrees: 30,
  defaultAngleDegrees: 330,
  connectedBondAngleDegrees: 120,
  proposalRadiusPx: 7,
  proposalHotDistancePx: 7,
  proposalSnapDistancePx: 7,
  existingEndpointOfferDistancePx: 14,
  existingEndpointSnapDistancePx: 7,
  touchTargetRadiusPx: 22,
  touchOfferDistancePx: 24
};

const ANGLE_EQUALITY_TOLERANCE_DEGREES = 0.1;

// A bond drawn onto the free end of a triple bond continues it in a straight line rather than
// turning by the usual interior angle -- sp carbons are linear.
const COLLINEAR_BOND_ANGLE_DEGREES = 180;

export function normalizedToPixel(point: NormalizedPoint, size: PaperSize): PixelPoint {
  return { x: point.x * size.width, y: point.y * size.height };
}

// #805: not clamped -- drawing reaches past the paper and the page grows to hold it (a click-only
// bond near the left or top edge used to be cut short at 0).
export function pixelToNormalized(point: PixelPoint, size: PaperSize): NormalizedPoint {
  return { x: point.x / size.width, y: point.y / size.height };
}

export function pointAtAngle(start: PixelPoint, lengthPx: number, angleDegrees: number): PixelPoint {
  const radians = (angleDegrees * Math.PI) / 180;
  return {
    x: start.x + Math.cos(radians) * lengthPx,
    y: start.y + Math.sin(radians) * lengthPx
  };
}

export function distanceBetween(first: PixelPoint, second: PixelPoint): number {
  return Math.hypot(second.x - first.x, second.y - first.y);
}

export const ATOM_ATTACH_DISTANCE_PX = 16;

export function nearestAtomWithin<T extends { readonly id: string; readonly position: NormalizedPoint }>(
  pointer: PixelPoint,
  atoms: readonly T[],
  size: PaperSize,
  maxDistancePx: number
): T | undefined {
  return atoms.reduce<T | undefined>((nearest, atom) => {
    const distance = distanceBetween(pointer, normalizedToPixel(atom.position, size));
    if (distance > maxDistancePx) return nearest;
    return !nearest || distance < distanceBetween(pointer, normalizedToPixel(nearest.position, size)) ? atom : nearest;
  }, undefined);
}

// The median (not mean) bond length, so a handful of unusually long or short bonds don't skew
// the result away from what most of the structure actually looks like.
export function typicalBondLengthPx(lines: readonly DrawingLine[], size: PaperSize): number {
  const lengths = lines
    .map((line) => distanceBetween(normalizedToPixel(line.start, size), normalizedToPixel(line.end, size)))
    .filter((length) => length > 0)
    .sort((first, second) => first - second);
  if (lengths.length === 0) return DEFAULT_BOND_DRAWING_CONFIG.lengthPx;
  const middle = Math.floor(lengths.length / 2);
  return lengths.length % 2 === 0 ? (lengths[middle - 1]! + lengths[middle]!) / 2 : lengths[middle]!;
}

const MINIMUM_BOND_STROKE_WIDTH_PX = 2;
const MAXIMUM_BOND_STROKE_WIDTH_PX = 7;

/**
 * Bond stroke width as a step function of rendered bond length, not a fixed constant: every
 * DEFAULT_BOND_DRAWING_CONFIG.lengthPx (45px) a bond's rendered length departs from the
 * standard, width steps by 1px, floored at the minimum and capped at the maximum. A standard
 * 45px bond resolves to exactly 3px, matching the app's prior fixed default.
 */
export function boundedBondStrokeWidthPx(renderedLengthPx: number): number {
  const steps = Math.max(0, Math.round(renderedLengthPx / DEFAULT_BOND_DRAWING_CONFIG.lengthPx));
  return Math.min(MAXIMUM_BOND_STROKE_WIDTH_PX, MINIMUM_BOND_STROKE_WIDTH_PX + steps);
}

export function proposedBondEndpoints(start: PixelPoint, config: BondDrawingConfig): readonly PixelPoint[] {
  const interval = Math.max(1, config.angleIntervalDegrees);
  const count = Math.max(1, Math.floor(360 / interval));
  return Array.from({ length: count }, (_, index) => pointAtAngle(start, config.lengthPx, index * interval));
}

function normalizeAngle(angleDegrees: number): number {
  return ((angleDegrees % 360) + 360) % 360;
}

function angleFrom(first: PixelPoint, second: PixelPoint): number {
  return normalizeAngle((Math.atan2(second.y - first.y, second.x - first.x) * 180) / Math.PI);
}

export function connectedBondAngle(
  endpoint: NormalizedPoint,
  lines: readonly DrawingLine[],
  size: PaperSize,
  bondAngleDegrees: number,
  bondOrderByLineId?: Readonly<Record<string, BondOrder>>,
  // The order of the bond being drawn. A triple bond makes the shared atom sp (linear), so the
  // new bond continues straight off its single neighbour -- the same 180 degrees as when the
  // *existing* neighbour is the triple.
  newBondOrder?: BondOrder,
  lengthPx = typicalBondLengthPx(lines, size)
): number {
  const endpointPixel = normalizedToPixel(endpoint, size);
  const neighbors = lines
    .flatMap((line) => {
      const start = normalizedToPixel(line.start, size);
      const end = normalizedToPixel(line.end, size);
      if (distanceBetween(endpointPixel, start) < 0.5) return [{ line, angle: angleFrom(endpointPixel, end) }];
      if (distanceBetween(endpointPixel, end) < 0.5) return [{ line, angle: angleFrom(endpointPixel, start) }];
      return [];
    })
    .sort((first, second) => first.angle - second.angle);

  if (neighbors.length === 0) return DEFAULT_BOND_DRAWING_CONFIG.defaultAngleDegrees;

  const occupiedAngles = neighbors.map((neighbor) => neighbor.angle);

  if (occupiedAngles.length === 1) {
    const existingAngle = occupiedAngles[0]!;
    // Straight continuation when either bond at the shared atom is a triple (sp, linear) -- the
    // existing neighbour or the one now being drawn; the caller's interior angle otherwise.
    const interiorAngle =
      bondOrderByLineId?.[neighbors[0]!.line.id] === 3 || newBondOrder === 3
        ? COLLINEAR_BOND_ANGLE_DEGREES
        : bondAngleDegrees;
    const candidates = [normalizeAngle(existingAngle - interiorAngle), normalizeAngle(existingAngle + interiorAngle)];
    const existingVerticalDirection = Math.sin((existingAngle * Math.PI) / 180);
    return candidates.reduce((best, candidate) => {
      const candidateVerticalDirection = Math.sin((candidate * Math.PI) / 180);
      const bestVerticalDirection = Math.sin((best * Math.PI) / 180);
      return candidateVerticalDirection * existingVerticalDirection > bestVerticalDirection * existingVerticalDirection
        ? candidate
        : best;
    });
  }

  // #1075: equal angular gaps at a fused ring junction can point into a ring.
  // Check the proposed shaft as well as its endpoint against non-incident bonds.
  const obstacles = lines.filter((line) => !neighbors.some((neighbor) => neighbor.line.id === line.id));
  const clearanceAt = (point: PixelPoint, line: DrawingLine) => {
    const a = normalizedToPixel(line.start, size),
      b = normalizedToPixel(line.end, size);
    const dx = b.x - a.x,
      dy = b.y - a.y;
    const squared = dx * dx + dy * dy;
    const t = squared ? Math.max(0, Math.min(1, ((point.x - a.x) * dx + (point.y - a.y) * dy) / squared)) : 0;
    return Math.hypot(point.x - a.x - t * dx, point.y - a.y - t * dy);
  };
  // Find ring interiors through paths connecting the incident neighbors without
  // revisiting the attachment atom. Clearance alone cannot distinguish an empty
  // ring interior from the exterior when both have room for a full bond.
  const vertices: PixelPoint[] = [];
  const adjacency: number[][] = [];
  const vertex = (point: PixelPoint) => {
    const found = vertices.findIndex((other) => distanceBetween(point, other) < 0.5);
    if (found >= 0) return found;
    vertices.push(point);
    adjacency.push([]);
    return vertices.length - 1;
  };
  for (const line of obstacles) {
    const a = vertex(normalizedToPixel(line.start, size)),
      b = vertex(normalizedToPixel(line.end, size));
    adjacency[a]!.push(b);
    adjacency[b]!.push(a);
  }
  const tip = (line: DrawingLine) => {
    const start = normalizedToPixel(line.start, size),
      end = normalizedToPixel(line.end, size);
    return distanceBetween(start, endpointPixel) < 0.5 ? end : start;
  };
  const polygons: PixelPoint[][] = [];
  neighbors.forEach((neighbor, index) => {
    const start = vertex(tip(neighbor.line)),
      target = vertex(tip(neighbors[(index + 1) % neighbors.length]!.line));
    const queue = [[start]],
      seen = new Set([start]);
    for (let i = 0; i < queue.length; i++) {
      const path = queue[i]!,
        at = path[path.length - 1]!;
      if (at === target) {
        polygons.push([endpointPixel, ...path.map((id) => vertices[id]!)]);
        break;
      }
      for (const next of adjacency[at] ?? [])
        if (!seen.has(next)) {
          seen.add(next);
          queue.push([...path, next]);
        }
    }
  });
  const insideRing = (point: PixelPoint) =>
    polygons.some((polygon) => {
      let inside = false;
      for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const a = polygon[i]!,
          b = polygon[j]!;
        if (a.y > point.y !== b.y > point.y && point.x < ((b.x - a.x) * (point.y - a.y)) / (b.y - a.y) + a.x)
          inside = !inside;
      }
      return inside;
    });
  let bestScore = -1;
  let widestGap = -1;
  let widestGapStart = occupiedAngles[0]!;
  occupiedAngles.forEach((angle, index) => {
    const next = index === occupiedAngles.length - 1 ? occupiedAngles[0]! + 360 : occupiedAngles[index + 1]!;
    const gap = next - angle;
    const candidateAngle = angle + gap / 2;
    const clearance = Math.min(
      lengthPx * 0.25,
      ...obstacles.flatMap((line) =>
        Array.from({ length: 16 }, (_, sample) =>
          clearanceAt(pointAtAngle(endpointPixel, (lengthPx * (sample + 1)) / 16, candidateAngle), line)
        )
      )
    );
    const ringPenalty = insideRing(pointAtAngle(endpointPixel, lengthPx * 0.1, candidateAngle)) ? 0.5 : 1;
    const score = (gap * clearance * ringPenalty) / (lengthPx * 0.25 || 1);
    if (
      score > bestScore + ANGLE_EQUALITY_TOLERANCE_DEGREES ||
      (Math.abs(score - bestScore) <= ANGLE_EQUALITY_TOLERANCE_DEGREES &&
        gap >= widestGap - ANGLE_EQUALITY_TOLERANCE_DEGREES)
    ) {
      bestScore = score;
      widestGap = gap;
      widestGapStart = angle;
    }
  });
  return normalizeAngle(widestGapStart + widestGap / 2);
}
